import { useCallback } from 'react';
import { Modal } from 'antd';
import { useAppDispatch } from '../store/hooks';
import { AsyncThunk } from '@reduxjs/toolkit';
import * as constant from '../actions/constant';

const { confirm } = Modal;

interface UseDeleteConfirmOptions {
  deleteAction: AsyncThunk<any, any, any>;
  idParamName?: string;
  title?: string;
  content?: string;
  onBeforeDelete?: () => void;
  onSuccess?: () => void;
}

interface UseDeleteConfirmReturn {
  showDeleteConfirm: (id: string, customTitle?: string) => void;
}

/**
 * Hook to show delete confirmation modal and dispatch delete action
 *
 * @param options - Configuration options
 * @param options.deleteAction - Redux async thunk to dispatch on confirm
 * @param options.idParamName - Parameter name for the ID (e.g., 'sportId', 'teamId')
 * @param options.title - Custom title for confirmation modal
 * @param options.content - Custom content for confirmation modal
 * @param options.onBeforeDelete - Callback before deletion is confirmed
 * @param options.onSuccess - Callback after successful deletion
 *
 * @example
 * const { showDeleteConfirm } = useDeleteConfirm({
 *   deleteAction: removeSport,
 *   idParamName: 'sportId',
 * });
 *
 * // In table row:
 * <Button onClick={() => showDeleteConfirm(item._id)}>Delete</Button>
 */
export function useDeleteConfirm(options: UseDeleteConfirmOptions): UseDeleteConfirmReturn {
  const {
    deleteAction,
    idParamName = 'id',
    title = constant.DELETE_RECORD,
    content = '',
    onBeforeDelete,
    onSuccess,
  } = options;

  const dispatch = useAppDispatch();

  const showDeleteConfirm = useCallback((id: string, customTitle?: string) => {
    confirm({
      title: customTitle || title,
      content,
      onOk() {
        onBeforeDelete?.();
        dispatch(deleteAction({ [idParamName]: id }));
        onSuccess?.();
      },
      onCancel() {},
    });
  }, [dispatch, deleteAction, idParamName, title, content, onBeforeDelete, onSuccess]);

  return { showDeleteConfirm };
}

export default useDeleteConfirm;
